import moment from 'moment';

/**
 * @typedef {Window & { __env__?: Record<string, string | undefined> }} EnvWindow
 */

const envWindow =
  typeof window !== 'undefined'
    ? /** @type {EnvWindow} */ (window)
    : /** @type {EnvWindow} */ ({});

const globalEnv = envWindow.__env__;

const withTrailingSlash = value => {
  const normalizedValue = value?.trim();

  return normalizedValue
    ? `${normalizedValue.replace(/\/+$/, '')}/`
    : '';
};

// NOTE: don't change this, use the REACT_APP_PROXY env var in development
export const API_BASE_URL = '/v1/';

export const REACT_APP_BUILD = globalEnv?.REACT_APP_BUILD || 'dev';

export const LUZMO_DASHBOARD_ID =
  globalEnv?.LUZMO_DASHBOARD_ID ||
  globalEnv?.LUZMO_V2_DASHBOARD_ID ||
  process.env.REACT_APP_LUZMO_DASHBOARD_ID ||
  process.env.REACT_APP_LUZMO_V2_DASHBOARD_ID ||
  '';
// Relative to API_BASE_URL; the authenticated UTTO API supplies the user context.
export const LUZMO_TOKEN_ENDPOINT = 'insights/token';
export const LUZMO_REPORTS_ENDPOINT = 'insights/reports';
export const LUZMO_SCHEDULE_REPORT_ENDPOINT = 'insights/reports/schedules';
export const LUZMO_EXPORT_REPORT_ENDPOINT = 'insights/reports/export';
export const LUZMO_APP_SERVER = withTrailingSlash(
  globalEnv?.LUZMO_APP_SERVER ||
  process.env.REACT_APP_LUZMO_APP_SERVER ||
  'https://app.us.luzmo.com/'
);
export const LUZMO_API_HOST = withTrailingSlash(
  globalEnv?.LUZMO_API_HOST ||
  process.env.REACT_APP_LUZMO_API_HOST ||
  'https://api.us.luzmo.com/'
);

// Google maps config
export const MAP_API_KEY = globalEnv?.GOOGLE_MAPS_API_KEY || '';
export const MAP_LIBRARIES =
  /** @type {import('@react-google-maps/api').Libraries} */ ([
    'geometry',
    'drawing',
    'places',
    'marker'
  ]);
export const MAP_URL =
  'https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,places&key=' +
  MAP_API_KEY;

export const MAP_TOKEN =
  'sk.eyJ1IjoiYnJhZGxleXV0dG8iLCJhIjoiY21rcXdlZHJoMHdhNzNrbXhscWFhN21xdyJ9.ioIjMsD2xwh2aj2ohRkVEw';

export const DEFAULT_MAP_BOUNDS = {
  lat: 52.324866,
  lon: -0.075902
};

export const ASSET_BOUNDS_DISTANCE = 2000; // 2km
export const ASSET_TIME_FRAME = 30; // 30 minutes

export const ENABLED_METRICS = [
  'centering',
  'lift',
  'depth',
  'gain',
  'aim',
  'null',
  'peak',
  'swing',
  'vertical',
  'compass',
  'current',
  'frequency',
];

export const DATE_RANGE_OPTIONS = [
  {
    label: 'Today',
    startDate: moment().startOf('day'),
    endDate: moment().endOf('day'),
    per: 'day'
  },
  {
    label: 'Yesterday',
    startDate: moment().subtract(1, 'days').startOf('day'),
    endDate: moment().subtract(1, 'days').endOf('day'),
    per: 'day'
  },
  {
    label: 'Last 7 Days',
    startDate: moment().subtract(7, 'days').startOf('day'),
    endDate: moment().endOf('day'),
    per: 'day',
  },
  {
    label: 'Last 30 Days',
    startDate: moment().subtract(30, 'days').startOf('day'),
    endDate: moment().endOf('day'),
    per: 'day',
  },
  {
    label: 'This Week',
    startDate: moment().startOf('week').startOf('day'),
    endDate: moment().endOf('day'),
    per: 'day',
  },
  {
    label: 'Last Week',
    startDate: moment().subtract(7, 'days').startOf('week').startOf('day'),
    endDate: moment().subtract(7, 'days').endOf('week').endOf('day'),
    per: 'day'
  },
  {
    label: 'This Month',
    startDate: moment().startOf('month').startOf('day'),
    endDate: moment().endOf('month').endOf('day'),
    per: 'week',
  },
  {
    label: 'Last Month',
    startDate: moment().subtract(1, 'month').startOf('month').startOf('day'),
    endDate: moment().subtract(1, 'month').endOf('month').endOf('day'),
    per: 'week'
  },
  {
    label: 'Last 3 Months',
    startDate: moment().subtract(3, 'month').startOf('month').startOf('day'),
    endDate: moment().endOf('day'),
    per: 'week'
  }
];

//DATE_RANGE_OPTIONS.forEach(x => {
  //console.log(`${x.label} ${x.startDate} ${x.endDate}`);
//});

export const DEFAULT_DATE_RANGE = DATE_RANGE_OPTIONS[3];

export const ROLES = {
  UTILITY_MANAGER: 'global.utility_manager',
  AREA_MANAGER: 'global.area_manager',
  TEAM_MANAGER: 'global.team_manager',
  FIELD_TECH: 'global.field_tech'
};

export const MANAGER_ROLES = [
  ROLES.UTILITY_MANAGER,
  ROLES.AREA_MANAGER,
  ROLES.TEAM_MANAGER
];

export const PERMISSIONS = {
  VIEW_FIELD_TECH_UI: 'global.view_field_tech_ui',
  VIEW_TEAM_UI: 'global.view_team_ui',
  VIEW_ADMIN_UI: 'global.view_admin_ui',
  VIEW_SINGLE_USER_DEMO_UI: 'global.view_single_user_demo_ui',
  VIEW_SYS_ADMIN_UI: 'global.view_system_admin_ui',
  VIEW_DIVISION_UI: 'global.view_division_ui',
  VIEW_UTILITY_UI: 'global.view_utility_ui',
  REQUEST_OTP: 'global.request_otp',
};

export const PERMISSIONS_WEIGHT = {
  [PERMISSIONS.VIEW_TEAM_UI]: 1,
  [PERMISSIONS.VIEW_DIVISION_UI]: 2,
  [PERMISSIONS.VIEW_UTILITY_UI]: 3,
  [PERMISSIONS.VIEW_ADMIN_UI]: 90,
  [PERMISSIONS.VIEW_SYS_ADMIN_UI]: 99
};

export const VIEW_TYPES = [
  {
    label: 'Administration',
    href: '/admin',
    permissions: [PERMISSIONS.VIEW_SYS_ADMIN_UI, PERMISSIONS.VIEW_ADMIN_UI]
  },
  {
    label: 'Area Manager',
    href: '/area',
    allowMultiple: true,
    permissions: [PERMISSIONS.VIEW_DIVISION_UI]
  },
  {
    label: 'Utility Manager',
    href: '/area',
    allowMultiple: true,
    permissions: [PERMISSIONS.VIEW_UTILITY_UI]
  },
  {
    label: 'Team Supervisor',
    href: '/team',
    allowMultiple: true,
    permissions: [
      PERMISSIONS.VIEW_TEAM_UI,
      PERMISSIONS.VIEW_SINGLE_USER_DEMO_UI
    ]
  },
  {
    label: 'My Locates',
    href: '/technician',
    allowMultiple: true,
    permissions: [PERMISSIONS.VIEW_FIELD_TECH_UI]
  }
];

export const SCORE_CLASSIFIER = score => {
  if (score === null) return 'na';
  if (score <= 40) return 'bad';
  if (score <= 70) return 'okay';
  return 'good';
};

export const EXPECTED_SCORE = 0.55;

export const SESSION_FILTERS = {
  PROBLEM: 1,
  OK: 2,
  GOOD: 3
};

export const SESSION_FILTERS_RANGE = {
  MIN: 40,
  MAX: 70
};

// RadioDetection extra menu items
export const rdExtra = 'UmFkaW9EZXRlY3Rpb24=';

export const FEATURE_ACCESS_ATTRIBUTE_MAP = {
  is_lpm: 'IS_LPM',
  mfa_required: 'MFA_REQUIRED',
  ai_training: 'FEATURE_AI_TRAINING',
  ai_insights: 'FEATURE_AI_INSIGHTS',
  lcd_replay: 'FEATURE_LCD_REPLAY',
  dp_predictions: 'FEATURE_AI_PREDICTIONS',
  vector_tile_layer: 'FEATURE_VECTOR_TILE_LAYER'
};
